/**
 * Helper function to normalize API responses
 * Handles responses that return HTTP 200 but contain internal error status codes (40X, 300+)
 * Also handles standard HTTP errors (like 500s)
 * 
 * The backend behavior:
 * - Errors 400-499: HTTP header returns 200, but body contains statusCode 40X
 * - Errors 500+: HTTP header returns 500, and may or may not have statusCode in body
 * 
 * @param {Object} responseData - The response data from the API
 * @param {Object} response - Optional response object (for HTTP status)
 * @param {string} defaultErrorMessage - Default error message if none found
 * @returns {Object} Normalized response with body and meta
 * @throws {Error} Error object with response data if status code indicates an error
 */
export const getApiResponse = (responseData, response = null, defaultErrorMessage = 'An error occurred.') => {
  if (!responseData) {
    throw new Error('Invalid response from server');
  }
  
  // Check HTTP status first (for 500+ errors)
  const httpStatus = response?.status || 200;
  
  // Then check internal statusCode in body (for 400-499 errors that come with HTTP 200)
  const statusCode = responseData.status_code || responseData.statusCode || httpStatus;
  const msgCode = responseData.msg_code || responseData.msgCode || [];
  const responseBody = responseData.body || null;
  const meta = responseData.meta || null;

  // Success: both HTTP status and internal statusCode are 2xx
  if (httpStatus >= 200 && httpStatus < 300 && statusCode >= 200 && statusCode < 300) {
    return {
      body: responseBody,
      meta: meta,
      status_code: statusCode,
    };
  }
  
  // Error: extract message from msgCode
  const errorMessage = msgCode.length > 0 
    ? (msgCode[0].description || msgCode[0].code || defaultErrorMessage)
    : defaultErrorMessage;
  
  const error = new Error(errorMessage);
  error.response = {
    data: responseData,
    status: statusCode, // Use internal statusCode (40X) if available, otherwise HTTP status (500+)
  };
  
  throw error;
};

/**
 * Helper function to handle API errors in catch blocks
 * Normalizes errors from axios/API calls
 * 
 * When axios throws an error with response.data (HTTP 500+), we reuse the same logic
 * as getApiResponse to normalize it. For network errors without response.data, we return the error as is.
 * 
 * @param {Error} error - The error object from catch block
 * @param {string} defaultErrorMessage - Default error message if none found
 * @returns {Error} Normalized error object
 */
export const handleApiError = (error, defaultErrorMessage = 'An error occurred.') => {
  // If error has response.data, it's an API error (HTTP 500+ or HTTP 200 with error in body)
  // Reuse the same normalization logic as getApiResponse
  if (error.response && error.response.data) {
    const responseData = error.response.data;
    const httpStatus = error.response.status || 200;
    const statusCode = responseData.status_code || responseData.statusCode || httpStatus;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    
    // Extract error message from msgCode (same logic as getApiResponse)
    const errorMessage = msgCode.length > 0 
      ? (msgCode[0].description || msgCode[0].code || defaultErrorMessage)
      : defaultErrorMessage;
    
    const apiError = new Error(errorMessage);
    apiError.response = {
      data: responseData,
      status: statusCode, // Use internal statusCode (40X) if available, otherwise HTTP status (500+)
    };
    return apiError;
  }

  // For network errors or other errors without response.data, return as is
  return error;
};

